import {
  FormControlLabel,
  Radio,
  RadioGroup,
  RadioGroupProps,
  Theme,
  alpha,
  useTheme,
} from '@mui/material'
import { ReactNode } from 'react'
import ZText from './ZText'
import { CARD_RADIUS, SPACE } from '../styles/layout'

interface ZRadioProps extends RadioGroupProps {
  options: Array<{
    value: string
    label: string
    subLabel?: string
    checked?: boolean
  }>
  box?: boolean
  renderAfterOption?: (optionValue: string) => ReactNode
}

const ZRadio = (props: ZRadioProps) => {
  const theme = useTheme<Theme>()

  const { box, options, renderAfterOption, ...rest } = props

  // Selectable card states (dark mode). Selected colours derive from the
  // org's primary colour so partner themes (e.g. WEEX) get matching cards.
  const primary = theme.palette.primary.main
  const card = {
    resting: { border: 'rgba(235, 235, 255, 0.18)' },
    selected: {
      border: alpha(primary, 0.6),
      ring: `0 0 0 1px ${alpha(primary, 0.22)}`,
      fill: alpha(primary, 0.08),
    },
  }

  const isSelected = (option: ZRadioProps['options'][number]) =>
    option.checked ?? (rest.value !== undefined && rest.value === option.value)

  return (
    <RadioGroup {...rest}>
      {options.map(option => (
        <div key={option.value}>
          <FormControlLabel
            value={option.value}
            control={
              <Radio
                checked={option.checked}
                style={{
                  color: theme.palette.primary.main,
                  // MUI gives the radio 9px of internal padding (a 42px hit
                  // area around a 24px icon). In a boxed card that padding
                  // added to the card's own padding, so the icon sat ~9px
                  // further from the left edge than the text did from the
                  // right. Pull it back so both edges read as the same inset.
                  marginLeft: box ? '-9px' : undefined,
                }}
              />
            }
            label={
              <div
                style={{
                  paddingTop: SPACE.sm,
                  paddingBottom: box ? SPACE.sm : undefined,
                }}
              >
                <ZText
                  // From the tablet breakpoint (sm, 640px) the title (18/27) and its
                  // description (16/24) need a gap of their own, or the line
                  // heights alone make them read as evenly spaced lines.
                  sx={
                    option.subLabel
                      ? { mb: { xs: 0, sm: SPACE['2xs'] } }
                      : undefined
                  }
                >
                  {option.label}
                </ZText>
                {option.subLabel ? (
                  <ZText style={{ fontWeight: '300' }} variant="body2">
                    {option.subLabel}
                  </ZText>
                ) : null}
              </div>
            }
            style={
              box
                ? {
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: isSelected(option)
                      ? card.selected.border
                      : card.resting.border,
                    boxShadow: isSelected(option)
                      ? card.selected.ring
                      : undefined,
                    backgroundColor: isSelected(option)
                      ? card.selected.fill
                      : undefined,
                    transition:
                      'border-color 120ms, box-shadow 120ms, background-color 120ms',
                    borderRadius: CARD_RADIUS,
                    // Vertical padding is 0.5rem because the label wrapper
                    // adds its own 0.5rem (which keeps the title level with
                    // the 42px radio box); together they make 1rem, matching
                    // the left inset. Right is a step wider so wrapped text
                    // clears the edge.
                    padding: `${SPACE.sm} 1.25rem ${SPACE.sm} 1rem`,
                    margin: `0 0 ${SPACE.xl} 0`,
                    width: '100%',
                    alignItems: 'flex-start',
                  }
                : {
                    margin: 0,
                    alignItems: 'flex-start',
                  }
            }
          />
          {renderAfterOption?.(option.value)}
        </div>
      ))}
    </RadioGroup>
  )
}

export default ZRadio
